import { useNavigate, type NavigateOptions } from "@solidjs/router"
import { createSignal } from "solid-js"

const SLUG_STORAGE_KEY = "cowork.workspace.slug"

export const APP_ROOTS = new Set([
  "admin",
  "agents",
  "approvals",
  "audit-log",
  "chats",
  "companies",
  "dashboard",
  "dashboards",
  "data-sources",
  "files",
  "integrations",
  "projects",
  "report-templates",
  "reports",
  "settings",
  "usage",
  "workflows",
])

const OTHER_ROOTS = new Set([
  "invite",
  "new-session",
  "onboarding",
  "register",
  "reset",
  "review",
  "server",
  "session",
  "shared",
  "shared-report",
  "verify",
])

export type WorkspacePath = { slug: string; rest: string }

export function splitWorkspacePath(pathname: string): WorkspacePath | undefined {
  const [first, second, ...rest] = pathname.split("/").filter(Boolean)
  if (!first || APP_ROOTS.has(first) || OTHER_ROOTS.has(first) || second === "session") return
  return { slug: first, rest: `/${[second, ...rest].filter(Boolean).join("/")}` }
}

export function appPath(pathname: string) {
  return splitWorkspacePath(pathname)?.rest ?? pathname
}

export function isAppPath(path: string) {
  return APP_ROOTS.has(path.split(/[/?#]/).filter(Boolean)[0] ?? "")
}

export function prefixWorkspace(slug: string, path: string) {
  if (!slug || !path.startsWith("/") || !isAppPath(path)) return path
  return `/${slug}${path}`
}

function storedSlug() {
  try {
    return localStorage.getItem(SLUG_STORAGE_KEY) ?? ""
  } catch {
    return ""
  }
}

const [slug, setSlug] = createSignal(storedSlug())

export const currentWorkspaceSlug = slug

export function rememberWorkspaceSlug(value: string) {
  setSlug(value)
  try {
    localStorage.setItem(SLUG_STORAGE_KEY, value)
  } catch {
    return
  }
}

export function workspaceHref(path: string) {
  return prefixWorkspace(slug(), path)
}

export function useAppNavigate() {
  const navigate = useNavigate()
  return (to: string, options?: Partial<NavigateOptions>) => navigate(workspaceHref(to), options)
}

export type RouteWorkspace = { slug: string; role: string; onboarded: boolean; company_ids: string[] }

export type WorkspaceDecision =
  | { kind: "render"; workspace?: RouteWorkspace; companyId?: string }
  | { kind: "redirect"; to: string; workspace?: RouteWorkspace; companyId?: string }
  | { kind: "onboarding"; workspace: RouteWorkspace; companyId?: string }

export function needsOnboarding(workspace: RouteWorkspace) {
  return !workspace.onboarded && (workspace.role === "owner" || workspace.role === "admin")
}

function companyIn(workspace: RouteWorkspace, activeCompanyId: string) {
  return workspace.company_ids.includes(activeCompanyId) ? activeCompanyId : workspace.company_ids[0]
}

export function resolveWorkspaceRoute(input: {
  pathname: string
  search: string
  workspaces: RouteWorkspace[]
  activeCompanyId: string
  rememberedSlug: string
}): WorkspaceDecision {
  const { pathname, search, workspaces, activeCompanyId, rememberedSlug } = input
  const home =
    workspaces.find((workspace) => workspace.company_ids.includes(activeCompanyId)) ??
    workspaces.find((workspace) => workspace.slug === rememberedSlug) ??
    workspaces[0]
  if (!home) return { kind: "render" }
  const homeCompany = companyIn(home, activeCompanyId)
  const dashboard = `/${home.slug}/dashboard`
  const first = pathname.split("/").filter(Boolean)[0]

  if (!first) {
    return {
      kind: "redirect",
      to: needsOnboarding(home) ? "/onboarding" : dashboard,
      workspace: home,
      companyId: homeCompany,
    }
  }
  if (first === "onboarding") {
    if (needsOnboarding(home)) return { kind: "onboarding", workspace: home, companyId: homeCompany }
    return { kind: "redirect", to: dashboard, workspace: home, companyId: homeCompany }
  }
  if (APP_ROOTS.has(first)) {
    return { kind: "redirect", to: `/${home.slug}${pathname}${search}`, workspace: home, companyId: homeCompany }
  }
  const route = splitWorkspacePath(pathname)
  if (!route) return { kind: "render", workspace: home }
  const target = workspaces.find((workspace) => workspace.slug === route.slug)
  if (!target) return { kind: "redirect", to: dashboard, workspace: home, companyId: homeCompany }
  const companyId = companyIn(target, activeCompanyId)
  if (needsOnboarding(target)) return { kind: "redirect", to: "/onboarding", workspace: target, companyId }
  return { kind: "render", workspace: target, companyId }
}

export function suggestSlug(name: string) {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32)
    .replace(/-+$/g, "")
}
